import React from "react";
import {
  ComposedChart,
  Line,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

import EmptyStateChart from "../../../Assets/performance-test-pre-state.svg";
const formatValue = (value, name) => {
  if (typeof value !== "number") return value;

  if (name === "Virtual Users") return Math.round(value);

  return value.toFixed(1);
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-3 border border-gray-200 shadow-lg rounded-lg text-xs">
        <p className="font-bold text-gray-700 mb-2">{label}</p>
        {payload.map((entry, index) => (
          <div key={index} className="flex items-center gap-2 mb-1">
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.color }} />
            <span className="text-gray-500 min-w-[100px]">{entry.name}:</span>
            <span className="font-medium text-gray-900">
              {formatValue(entry.value, entry.name)}
              {entry.unit}
            </span>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

const PerformanceMetricsGraph = ({ data }) => {
  if (!data || data.length === 0) {
    return (
      <div className="h-[400px] w-full bg-gradient-to-br from-gray-50 to-blue-50/30 border border-gray-200 rounded-lg flex flex-col items-center justify-center text-gray-400 relative overflow-hidden">
        <div className="w-64 h-64 mb-4">
          <img
            src={EmptyStateChart}
            alt="No data available"
            className="w-full h-full object-contain"
          />
        </div>

        <div className="text-center z-10">
          <h3 className="text-lg font-semibold text-gray-700 mb-2">No Performance Data Yet</h3>
          <p className="text-sm text-gray-500 mb-4">
            Start a load test to see real-time metrics and performance graphs
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
      <div className="h-[350px] w-full font-sans">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 10, right: 0, left: -20, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />

            <XAxis
              dataKey="timestamp"
              tick={{ fontSize: 11, fill: "#6b7280" }}
              tickLine={false}
              axisLine={{ stroke: "#e5e7eb" }}
              dy={10}
            />

            {/* Left Y-Axis: Virtual Users & RPS */}
            <YAxis
              yAxisId="left"
              tick={{ fontSize: 11, fill: "#6b7280" }}
              tickLine={false}
              axisLine={false}
              tickFormatter={(val) => (typeof val === "number" ? val.toFixed(0) : val)}
              label={{
                value: "Count",
                angle: -90,
                position: "insideLeft",
                style: { fill: "#9ca3af", fontSize: 10 },
                offset: 10,
              }}
            />

            {/* Right Y-Axis: Response Time (Blue to match line) */}
            <YAxis
              yAxisId="right"
              orientation="right"
              tick={{ fontSize: 11, fill: "#2563eb" }}
              tickLine={false}
              axisLine={false}
              tickFormatter={(val) => `${Number(val).toFixed(0)}ms`}
              domain={["auto", "auto"]}
            />

            <Tooltip
              content={<CustomTooltip />}
              cursor={{ stroke: "#9ca3af", strokeWidth: 1, strokeDasharray: "3 3" }}
            />

            <Legend
              verticalAlign="bottom"
              height={36}
              iconType="plainline"
              wrapperStyle={{ paddingTop: "20px", fontSize: "12px" }}
            />

            <Area
              yAxisId="left"
              type="monotone"
              dataKey="currentVirtualUsers"
              name="Virtual Users"
              fill="#f3f4f6"
              stroke="#d1d5db"
              strokeWidth={1}
              fillOpacity={1}
              unit=""
            />

            {/* 2. Requests Per Second (Amber Line) */}
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="currentRps"
              name="Requests/sec"
              stroke="#f59e0b"
              strokeWidth={2}
              dot={false}
              unit=" rps"
            />

            {/* 3. Error Rate (Red Line) */}
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="errorRate"
              name="Error Rate"
              stroke="#ef4444"
              strokeWidth={2}
              dot={false}
              unit="%"
            />

            {/* 4. Avg Response Time (Blue Line - Primary Focus) */}
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="avgResponseTime"
              name="Avg. Response"
              stroke="#2563eb"
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4, strokeWidth: 0 }}
              unit=" ms"
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default PerformanceMetricsGraph;
